import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  AppFile,
  InvoiceStatus,
  QuickbooksCreditMemo,
  QuickbooksCreditMemoSchema,
  QuickbooksInvoice,
  QuickbooksInvoiceSchema,
  QuickbooksJob,
  formatMoney,
} from '@dill/dill-shared';
import type { InvoiceTabs } from './ViewSupplierInvoices';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  selectInvoice,
  unSelectInvoice,
  selectCreditMemo,
  unSelectCreditMemo,
} from '../../../../redux/globalSlices/invoiceSlice';
import {
  resetInvoiceBase64,
  selectInvoiceIdsForDownload,
  unSelectInvoiceIdsForDownload,
} from '../../../../redux/globalSlices/buyersSlice';
import { DillButton, DillCheckbox, DillDropdown, DillDropdownButton } from '../../../general/Dill';
import AppNotesModal from '../../../general/AppNotesModal/AppNotesModal';
import AppLastSyncDataView from '../../../general/AppLastSyncDataView/AppLastSyncDataView';
import { sendMessagesInvoice } from '../../../../redux/services/invoicesService';
import { getDocumentPdf } from '../../../../redux/services/buyersService';
import {
  downloadFile,
  firebaseFileUploadHelper,
  openDillToast,
  printFile,
} from '../../../../utils/helpers';
import { weeksOptions } from '../../../../utils/constants';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { AppPdfListModal } from '../../../general/AppPdfListModal/AppPdfListModal';
import {
  ArrowsDownUp,
  Buildings,
  CaretDown,
  Chat,
  DownloadSimple,
  Eye,
  FilePlus,
  Info,
  Printer,
  X,
} from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

const NET_TERMS_REGEX = /^(\d+%\s?.*|.*\s?\d+%)$/;
const CVS_PLUMBING_DISCOUNT_TERMS = ['210N25', '210N30', '210N30X15'];
const DISCOUNT_INTEGRATIONS = [
  'MANUAL_UPLOAD',
  'DDI_MANUAL_UPLOAD',
  'P21_MANUAL_UPLOAD',
  'SAGE_100',
  'DISTRIBUTION_ONE',
  'RUTTER_QBD',
  'ECLIPSE',
  'ECLIPSE_MANUAL_UPLOAD',
  'EAGLE_MANUAL_UPLOAD',
];

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SummaryDetail {
  key: string;
  icon?: ReactNode;
  value: string;
  line1: string;
  line2: string;
  invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[];
}

export interface ViewSupplierInvoicesMobileProps {
  invoicesTabs: InvoiceTabs[];
  selectedTabId: InvoiceStatus;
  onTabChange: (tabId: InvoiceStatus) => void;
  searchText: string;
  onSearchChange: (text: string) => void;
  filteredInvoices: (QuickbooksInvoice | QuickbooksCreditMemo)[];
  summaryDetails: SummaryDetail[];
  onPaySingle: (invoice: QuickbooksInvoice) => void;
  onBulkPay: () => void;
  onBulkDownload: (combined: boolean) => void;
  onView: (doc: QuickbooksInvoice | QuickbooksCreditMemo) => void;
  buyerUserCanPay: boolean;
  isSupplierVerified: boolean;
  accumulateAmount: number;
  selectedSupplierJobs: QuickbooksJob[];
  selectedJobIds: string[];
  onJobIdsChange: (ids: string[]) => void;
  selectedInvoiceFilterId: 'SHOW_OPEN' | 'SHOW_PAST_DUE' | null;
  onInvoiceFilterChange: (id: 'SHOW_OPEN' | 'SHOW_PAST_DUE' | null) => void;
  selectedMainBuyerId: string | null;
  supplierId: string;
  supplierDomainId: string;
  buyerSupplierInvoicesDataWeeksLimit: number;
  onWeeksLimitChange: (weeks: number) => void;
  statementPdfsOptions: { label: string; value: string; extraData?: string }[];
  onDownloadStatement: () => void;
}

interface DocDisplayInfo {
  docId: string;
  typeName: string;
  typeVariant: 'invoice' | 'finance' | 'credit' | 'payment' | 'misc' | 'debit';
  amount: number;
  open: number;
  dueDate: string;
  date: string;
  terms: string;
  poNumber: string;
  job: string;
  address: string;
  isOverdue: boolean;
  canPay: boolean;
  // PDF-related
  isPdfAvailable: boolean;
  isInvoice: boolean;
  id: string;
  docNumber: string;
  signedPdfs: AppFile[] | null | undefined;
  docType: 'INVOICE' | 'CREDIT_MEMO';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getDocDisplayInfo(doc: QuickbooksInvoice | QuickbooksCreditMemo): DocDisplayInfo {
  const invoiceCheck = QuickbooksInvoiceSchema.safeParse(doc);
  if (invoiceCheck.success) {
    const inv = invoiceCheck.data;
    const jobParts: string[] = [];
    if (inv.quickBooksJob?.jobNumber) jobParts.push(inv.quickBooksJob.jobNumber);
    if (inv.quickBooksJob?.jobName) jobParts.push(inv.quickBooksJob.jobName);
    return {
      docId: inv.docNumber || inv.id,
      typeName:
        inv.recordType === 'FINANCE_CHARGE'
          ? 'Finance Charge'
          : inv.recordType === 'DEBIT_MEMO'
          ? 'Debit Memo'
          : 'Invoice',
      typeVariant:
        inv.recordType === 'FINANCE_CHARGE'
          ? 'finance'
          : inv.recordType === 'DEBIT_MEMO'
          ? 'debit'
          : 'invoice',
      amount: inv.amount ?? 0,
      open: inv.balance ?? 0,
      dueDate: inv.dueDate ? moment(inv.dueDate).format('MM/DD/YYYY') : '—',
      date: inv.createdAt ? moment(inv.createdAt).format('MM/DD/YYYY') : '—',
      terms: inv.terms || '—',
      poNumber: inv.poNumber || '—',
      job: jobParts.join(' · ') || '—',
      address: inv.quickBooksJob?.address || '',
      isOverdue: inv.status === 'PAST_DUE',
      canPay: true,
      isPdfAvailable: !!inv.isPdfAvailable,
      isInvoice: true,
      id: inv.id,
      docNumber: inv.docNumber || '',
      signedPdfs: inv.dillInvoice?.signedPdfs,
      docType: 'INVOICE',
    };
  }
  const cm = doc as QuickbooksCreditMemo;
  const jobParts: string[] = [];
  if (cm.quickBooksJob?.jobNumber) jobParts.push(cm.quickBooksJob.jobNumber);
  const jobName = cm.quickBooksJob?.jobName || cm.jobName;
  if (jobName) jobParts.push(jobName);

  let typeName = 'Credit Memo';
  let typeVariant: DocDisplayInfo['typeVariant'] = 'credit';
  if (cm.recordType === 'PAYMENT') {
    typeName = 'Payment';
    typeVariant = 'payment';
  } else if (cm.recordType === 'MISC_CREDITS') {
    typeName = 'Misc Credits';
    typeVariant = 'misc';
  } else if (cm.recordType === 'CREDIT_INVOICE') {
    typeName = 'Credits Invoice';
    typeVariant = 'credit';
  }

  // Negative prefix for amount: applies to CREDIT_MEMO, PAYMENT, MISC_CREDITS (matches desktop)
  const negateAmount =
    cm.recordType === 'CREDIT_MEMO' ||
    cm.recordType === 'PAYMENT' ||
    cm.recordType === 'MISC_CREDITS';
  const rawAmount = cm.totalAmount ?? 0;
  const rawBalance = cm.balance ?? 0;

  return {
    docId: cm.docNumber || cm.id,
    typeName,
    typeVariant,
    amount: negateAmount ? -rawAmount : rawAmount,
    open: -rawBalance,
    dueDate: cm.dueDate ? moment(cm.dueDate).format('MM/DD/YYYY') : '—',
    date: cm.createdAt ? moment(cm.createdAt).format('MM/DD/YYYY') : '—',
    terms: '—',
    poNumber: '—',
    job: jobParts.join(' · ') || '—',
    address: cm.quickBooksJob?.address || '',
    isOverdue: false,
    canPay: false,
    isPdfAvailable: !!cm.isPdfAvailable,
    isInvoice: false,
    id: cm.id,
    docNumber: cm.docNumber || '',
    signedPdfs: undefined,
    docType: 'CREDIT_MEMO',
  };
}

// ─── Sort ─────────────────────────────────────────────────────────────────────
type SortField = 'dueDate' | 'amount' | 'open' | 'date';
type SortDir = 'asc' | 'desc';

const SORT_OPTIONS: { label: string; field: SortField; dir: SortDir }[] = [
  { label: 'Due Date — Soonest first', field: 'dueDate', dir: 'asc' },
  { label: 'Due Date — Latest first', field: 'dueDate', dir: 'desc' },
  { label: 'Amount — Highest first', field: 'amount', dir: 'desc' },
  { label: 'Amount — Lowest first', field: 'amount', dir: 'asc' },
  { label: 'Open Amount — Highest first', field: 'open', dir: 'desc' },
  { label: 'Open Amount — Lowest first', field: 'open', dir: 'asc' },
  { label: 'Invoice Date — Newest first', field: 'date', dir: 'desc' },
  { label: 'Invoice Date — Oldest first', field: 'date', dir: 'asc' },
];

function SortSheet({
  sortField,
  sortDir,
  onSelect,
  onClear,
  onClose,
}: {
  sortField: SortField | null;
  sortDir: SortDir;
  onSelect: (field: SortField, dir: SortDir) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-[14px] font-medium text-gray-900">Sort by</span>
          <div className="flex items-center gap-3">
            {sortField && (
              <button onClick={onClear} className="text-[13px] text-blue-500">
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-[20px] leading-none">
              ×
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-2">
          {SORT_OPTIONS.map((opt) => {
            const isSelected = sortField === opt.field && sortDir === opt.dir;
            return (
              <button
                key={`${opt.field}-${opt.dir}`}
                onClick={() => {
                  onSelect(opt.field, opt.dir);
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left">
                <div
                  className={[
                    'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 border',
                    isSelected ? 'border-blue-500' : 'border-gray-300',
                  ].join(' ')}>
                  {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                </div>
                <span className="text-[13px] text-gray-900">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="shrink-0">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 22V12h6v10M3 9h18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── Primitive components ─────────────────────────────────────────────────────
function Field({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-gray-400 mb-0.5">{label}</div>
      <div className={`text-[12px] ${danger ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

function TypeBadge({
  variant,
  label,
}: {
  variant: 'invoice' | 'finance' | 'credit' | 'payment' | 'misc' | 'debit';
  label: string;
}) {
  const styles = {
    invoice: 'bg-green-50 text-green-700',
    finance: 'bg-amber-50 text-amber-700',
    credit: 'bg-blue-50 text-blue-700',
    payment: 'bg-purple-50 text-purple-700',
    misc: 'bg-orange-50 text-orange-700',
    debit: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${styles[variant]}`}>
      {label}
    </span>
  );
}

// ─── Invoice card ─────────────────────────────────────────────────────────────
function InvoiceCard({
  info,
  checked,
  onCheck,
  isOpen,
  onToggle,
  onPay,
  onView,
  onNote,
  onDownload,
  onPrint,
  canPay,
  selectable,
  disabledReason,
  discountAvailable,
}: {
  info: DocDisplayInfo;
  checked: boolean;
  onCheck: () => void;
  isOpen: boolean;
  onToggle: () => void;
  onPay: () => void;
  onView: () => void;
  onNote: () => void;
  onDownload: () => void;
  onPrint: () => void;
  canPay: boolean;
  selectable: boolean;
  disabledReason?: string;
  discountAvailable: boolean;
}) {
  return (
    <div
      className={[
        'bg-white rounded-xl border px-4 py-3',
        checked ? 'border-blue-500' : 'border-gray-100',
      ].join(' ')}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {selectable ? (
            <DillCheckbox isChecked={checked} onChange={onCheck} />
          ) : disabledReason ? (
            <>
              <span
                data-tooltip-id={'disabled-invoice-' + info.id}
                className="flex items-center text-gray-400 cursor-help">
                <Info size={14} />
              </span>
              <ReactTooltip
                id={'disabled-invoice-' + info.id}
                place="right"
                variant="dark"
                opacity="100%"
                style={{ background: '#222', maxWidth: '220px', zIndex: 99999 }}>
                <span>{disabledReason}</span>
              </ReactTooltip>
            </>
          ) : null}
          <span className="text-[13px] font-medium text-gray-900">{info.docId}</span>
          <TypeBadge variant={info.typeVariant} label={info.typeName} />
        </div>
        <button
          onClick={onToggle}
          aria-label="Toggle details"
          className="text-gray-300 hover:text-gray-500 p-1 rounded transition-colors">
          <CaretDown
            size={14}
            style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-4 gap-2 mb-2">
        <Field
          label="Amount"
          value={`${info.amount < 0 ? '-' : ''}$${Math.abs(info.amount).toFixed(2)}`}
        />
        <Field
          label="Open"
          value={`${info.open < 0 ? '-' : ''}$${Math.abs(info.open).toFixed(2)}`}
        />
        <Field label="Due date" value={info.dueDate} danger={info.isOverdue} />
        <Field label="Terms" value={info.terms} />
      </div>

      {/* Expanded fields */}
      {isOpen && (
        <div className="grid grid-cols-4 gap-2 mb-2 pt-2 border-t border-gray-100">
          <Field label="Date" value={info.date} />
          <Field label="PO number" value={info.poNumber} />
        </div>
      )}

      {/* Job row */}
      {(info.job !== '—' || info.address) && (
        <div className="flex items-center gap-1.5 text-[12px] text-gray-400 pt-2 border-t border-gray-100 mb-2">
          <BuildingIcon />
          <span className="truncate">
            {info.job}
            {info.address ? ` — ${info.address}` : ''}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        {info.isPdfAvailable && (
          <DillButton
            variant="icon-default"
            icon={<Eye size={16} />}
            onClick={onView}
            height={32}
            width={32}
          />
        )}
        {info.isPdfAvailable && (
          <DillButton
            variant="icon-default"
            icon={<DownloadSimple size={16} />}
            onClick={onDownload}
            height={32}
            width={32}
          />
        )}
        {info.isPdfAvailable && (
          <DillButton
            variant="icon-default"
            icon={<Printer size={16} />}
            onClick={onPrint}
            height={32}
            width={32}
          />
        )}
        {canPay && info.isInvoice && (
          <DillButton
            variant="icon-default"
            icon={<Chat size={16} />}
            onClick={onNote}
            height={32}
            width={32}
          />
        )}
        {canPay && (
          <div className="ml-auto flex flex-col items-end gap-0.5">
            <DillButton label={`Pay $${info.open.toFixed(2)}`} onClick={onPay} height={32} />
            {discountAvailable && (
              <>
                <p
                  data-tooltip-id={'possible-discount-' + info.id}
                  className="text-[9px] text-right PRIMARY_500-CLR cursor-default">
                  Possible Discount
                </p>
                <ReactTooltip
                  id={'possible-discount-' + info.id}
                  place="top"
                  variant="dark"
                  opacity="100%"
                  style={{ background: '#222', width: '240px', zIndex: 99999 }}>
                  <span>
                    Click Pay, and any discounts will get automatically applied for you when you
                    select your payment method.
                  </span>
                </ReactTooltip>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Jobs filter sheet ────────────────────────────────────────────────────────
function JobsFilterSheet({
  jobs,
  selectedIds,
  onClose,
  onToggleJob,
  onClear,
}: {
  jobs: QuickbooksJob[];
  selectedIds: string[];
  onClose: () => void;
  onToggleJob: (id: string) => void;
  onClear: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl max-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-[14px] font-medium text-gray-900">Filter by Job</span>
          <div className="flex items-center gap-3">
            {selectedIds.length > 0 && (
              <button onClick={onClear} className="text-[13px] text-blue-500">
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-[20px] leading-none">
              ×
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-2">
          {jobs.map((job) => {
            const isSelected = selectedIds.includes(job.id);
            const label = job.jobNumber ? `${job.jobNumber} – ${job.jobName}` : job.jobName;
            const address = [job.address, job.city, job.state].filter(Boolean).join(', ');
            return (
              <button
                key={job.id}
                onClick={() => onToggleJob(job.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left">
                <div
                  className={[
                    'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border',
                    isSelected
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-gray-300',
                  ].join(' ')}>
                  {isSelected && <CheckIcon />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-gray-900 truncate">{label}</div>
                  {address && <div className="text-[11px] text-gray-400 truncate">{address}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function ViewSupplierInvoicesMobile({
  invoicesTabs,
  selectedTabId,
  onTabChange,
  searchText,
  onSearchChange,
  filteredInvoices,
  summaryDetails,
  onPaySingle,
  onBulkPay,
  onBulkDownload,
  onView,
  buyerUserCanPay,
  isSupplierVerified,
  accumulateAmount,
  selectedSupplierJobs,
  selectedJobIds,
  onJobIdsChange,
  selectedInvoiceFilterId,
  onInvoiceFilterChange,
  selectedMainBuyerId,
  supplierId,
  supplierDomainId,
  buyerSupplierInvoicesDataWeeksLimit,
  onWeeksLimitChange,
  statementPdfsOptions,
  onDownloadStatement,
}: ViewSupplierInvoicesMobileProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedInvoices, selectedCreditMemos, selectedSupplierForBuyer } = useAppSelector(
    (state) => state.invoice
  );
  const { invoiceIdsForDownload, previewPdf, invoiceBase64 } = useAppSelector(
    (state) => state.buyers
  );

  const isCreditMemoSelectable = (
    creditMemo: QuickbooksCreditMemo,
    integrationType: string | undefined
  ) =>
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'SAGE_100') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'MISC_CREDITS' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'INFOR_DIST_SX') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DISTRIBUTION_ONE') ||
    (creditMemo.recordType === 'CREDIT_MEMO' && integrationType === 'MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'ABLE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DDI_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'MARKS_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_QBD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_NETSUITE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'EAGLE_MANUAL_UPLOAD') ||
    integrationType === 'QBO';

  const [expanded, setExpanded] = useState(new Set<string>());
  const [showJobsSheet, setShowJobsSheet] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showSortSheet, setShowSortSheet] = useState(false);

  // Note modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteInvoice, setNoteInvoice] = useState<QuickbooksInvoice | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteCategory, setNoteCategory] = useState('Other');
  const noteFilesRef = useRef<File[]>([]);

  // PDF state
  const [openPdfPreviewModal, setOpenPdfPreviewModal] = useState(false);
  const [openPdfListModal, setOpenPdfListModal] = useState(false);
  const [downloadPdfList, setDownloadPdfList] = useState<any[]>([]);
  const [previewPdfList, setPreviewPdfList] = useState<any[]>([]);
  const [selectedDocNumber, setSelectedDocNumber] = useState('');
  const [printMode, setPrintMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [invoiceSigned, setInvoiceSigned] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<QuickbooksInvoice | null>(null);
  const [activeCreditMemo, setActiveCreditMemo] = useState<QuickbooksCreditMemo | null>(null);

  const activeJobIds = selectedJobIds.filter((id) => id !== 'ALL');

  const isPaidOrPending = selectedTabId === 'PAID' || selectedTabId === 'PENDING_PAY';

  useEffect(() => {
    if (invoiceBase64) {
      window.open('data:application/pdf;base64, ' + invoiceBase64, '_blank');
      dispatch(resetInvoiceBase64());
    }
  }, [invoiceBase64]);

  useEffect(() => {
    if (previewPdf && previewMode) {
      setInvoiceSigned(false);
      setPreviewPdfList([{ url: previewPdf, name: 'pdf', date: new Date() }]);
      setOpenPdfPreviewModal(true);
    }
  }, [previewPdf]);

  const handlePreviewPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | null;
  }) => {
    setSelectedDocNumber(docNumber);
    setPreviewMode(true);
    setPrintMode(false);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        if (!b.date || !a.date) return 0;
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      setInvoiceSigned(true);
      setPreviewPdfList(sortedPdfs);
      setOpenPdfPreviewModal(true);
    } else {
      const res = await dispatch(getDocumentPdf({ supplierDomainId, id, type, preview: true }));
      if (res && res.type !== 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'Failed to load invoice pdf', type: 'ERROR' });
      }
    }
  };

  const handleDownloadPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | null;
  }) => {
    setPreviewMode(false);
    setPrintMode(false);
    setSelectedDocNumber(docNumber);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        if (!b.date || !a.date) return 0;
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      if (sortedPdfs.length > 1) {
        setDownloadPdfList(sortedPdfs);
        setOpenPdfListModal(true);
      } else {
        downloadFile(sortedPdfs[0].url || '', `invoice-${docNumber}-${+new Date()}.pdf`);
      }
    } else {
      const res = await dispatch(getDocumentPdf({ supplierDomainId, id, type }));
      if (res && res.type === 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'Invoice pdf successfully downloaded', type: 'SUCCESS' });
      } else {
        openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
      }
    }
  };

  const handlePrintPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | null;
  }) => {
    setPreviewMode(false);
    setPrintMode(true);
    setSelectedDocNumber(docNumber);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        if (!b.date || !a.date) return 0;
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      if (sortedPdfs.length > 1) {
        setDownloadPdfList(sortedPdfs);
        setOpenPdfListModal(true);
      } else {
        printFile(sortedPdfs[0].url || '', `invoice-${docNumber}-${+new Date()}.pdf`);
      }
    } else {
      const res = await dispatch(getDocumentPdf({ supplierDomainId, id, type, print: true }));
      if (res && res.type !== 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'Error printing the invoice pdf', type: 'ERROR' });
      }
    }
  };

  const handleDownloadSelectedPdf = (url: string) => {
    if (printMode) {
      printFile(url, `invoice-${selectedDocNumber}-${+new Date()}.pdf`);
    } else {
      downloadFile(url, `invoice-${selectedDocNumber}-${+new Date()}.pdf`);
    }
  };

  const currentTabDownloadIds = useMemo(
    () => invoiceIdsForDownload[selectedTabId] ?? [],
    [invoiceIdsForDownload, selectedTabId]
  );

  const currentTabCreditMemoIds = useMemo(
    () => (selectedCreditMemos[selectedTabId] ?? []).map((cm) => cm.id),
    [selectedCreditMemos, selectedTabId]
  );

  const currentTabSelectedIds = useMemo(
    () => new Set<string>([...currentTabDownloadIds, ...currentTabCreditMemoIds]),
    [currentTabDownloadIds, currentTabCreditMemoIds]
  );

  const integrationType = selectedSupplierForBuyer?.integrationType ?? undefined;

  const hasDiscountAvailable = useMemo(() => {
    if (!DISCOUNT_INTEGRATIONS.includes(integrationType ?? '')) return false;
    const openInvoices = selectedInvoices[selectedTabId] ?? [];
    return openInvoices.some((inv) => NET_TERMS_REGEX.test(inv.terms ?? ''));
  }, [selectedInvoices, selectedTabId, integrationType]);

  const selectableDocs = useMemo(() => {
    return filteredInvoices.filter((doc) => {
      if (QuickbooksInvoiceSchema.safeParse(doc).success) return true;
      const cm = QuickbooksCreditMemoSchema.safeParse(doc);
      return cm.success && isCreditMemoSelectable(cm.data, integrationType);
    });
  }, [filteredInvoices, integrationType]);

  const selectAll =
    selectableDocs.length > 0 && selectableDocs.every((doc) => currentTabSelectedIds.has(doc.id));
  const indeterminate =
    !selectAll && selectableDocs.some((doc) => currentTabSelectedIds.has(doc.id));

  const toggleAll = () => {
    const invoices = selectableDocs
      .filter((doc) => QuickbooksInvoiceSchema.safeParse(doc).success)
      .map((doc) => QuickbooksInvoiceSchema.parse(doc));
    const creditMemos = selectableDocs
      .filter((doc) => {
        const cm = QuickbooksCreditMemoSchema.safeParse(doc);
        return cm.success && isCreditMemoSelectable(cm.data, integrationType);
      })
      .map((doc) => QuickbooksCreditMemoSchema.parse(doc));
    const invoiceIds = invoices.map((i) => i.id);

    if (selectAll) {
      if (!isPaidOrPending) {
        dispatch(unSelectInvoice({ tabId: selectedTabId, invoices }));
      }
      dispatch(unSelectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds }));
      dispatch(unSelectCreditMemo({ tabId: selectedTabId, creditMemos }));
    } else {
      if (!isPaidOrPending) {
        dispatch(selectInvoice({ tabId: selectedTabId, invoices }));
      }
      dispatch(selectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds }));
      dispatch(selectCreditMemo({ tabId: selectedTabId, creditMemos }));
    }
  };

  const toggleSelect = useCallback(
    (doc: QuickbooksInvoice | QuickbooksCreditMemo) => {
      const invoiceCheck = QuickbooksInvoiceSchema.safeParse(doc);
      if (invoiceCheck.success) {
        const inv = invoiceCheck.data;
        const isSelected = currentTabDownloadIds.includes(inv.id);
        if (isSelected) {
          if (!isPaidOrPending) {
            dispatch(unSelectInvoice({ tabId: selectedTabId, invoices: [inv] }));
          }
          dispatch(unSelectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds: [inv.id] }));
        } else {
          if (!isPaidOrPending) {
            dispatch(selectInvoice({ tabId: selectedTabId, invoices: [inv] }));
          }
          dispatch(selectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds: [inv.id] }));
        }
        return;
      }
      const cmCheck = QuickbooksCreditMemoSchema.safeParse(doc);
      if (cmCheck.success && isCreditMemoSelectable(cmCheck.data, integrationType)) {
        const cm = cmCheck.data;
        const isSelected = currentTabCreditMemoIds.includes(cm.id);
        if (isSelected) {
          dispatch(unSelectCreditMemo({ tabId: selectedTabId, creditMemos: [cm] }));
        } else {
          dispatch(selectCreditMemo({ tabId: selectedTabId, creditMemos: [cm] }));
        }
      }
    },
    [
      dispatch,
      selectedTabId,
      currentTabDownloadIds,
      currentTabCreditMemoIds,
      isPaidOrPending,
      integrationType,
    ]
  );

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setExpanded(new Set());
    setSortField(null);
  }, [selectedTabId]);

  const handleSortSelect = useCallback((field: SortField, dir: SortDir) => {
    setSortField(field);
    setSortDir(dir);
  }, []);

  const handleSortClear = useCallback(() => {
    setSortField(null);
    setSortDir('asc');
  }, []);

  const sortedInvoices = useMemo(() => {
    if (!sortField) return filteredInvoices;
    return [...filteredInvoices].sort((a, b) => {
      let aVal = 0;
      let bVal = 0;
      if (sortField === 'dueDate') {
        const aDue = (a as QuickbooksInvoice).dueDate;
        const bDue = (b as QuickbooksInvoice).dueDate;
        aVal = aDue ? new Date(aDue).getTime() : 0;
        bVal = bDue ? new Date(bDue).getTime() : 0;
      } else if (sortField === 'amount') {
        aVal = getDocDisplayInfo(a).amount;
        bVal = getDocDisplayInfo(b).amount;
      } else if (sortField === 'open') {
        aVal = getDocDisplayInfo(a).open;
        bVal = getDocDisplayInfo(b).open;
      } else {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredInvoices, sortField, sortDir]);

  const handleBucketClick = useCallback(
    (summary: SummaryDetail, alreadyAllSelected: boolean) => {
      if (!summary.invoices || summary.invoices.length === 0) return;
      const parsedInvoices = summary.invoices
        .filter((inv) => {
          const check = QuickbooksInvoiceSchema.safeParse(inv);
          if (!check.success) return false;
          const isPendingReconciliation = check.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
          const hasNegativeBalance = (check.data.balance ?? 0) < 0;
          return !(isPendingReconciliation && hasNegativeBalance);
        })
        .map((inv) => QuickbooksInvoiceSchema.parse(inv));
      // Buckets are built from open items, so their credits are not on the paid/pending tabs.
      const parsedCreditMemos = isPaidOrPending
        ? []
        : summary.invoices
            .filter((doc) => {
              const cm = QuickbooksCreditMemoSchema.safeParse(doc);
              return cm.success && isCreditMemoSelectable(cm.data, integrationType);
            })
            .map((doc) => QuickbooksCreditMemoSchema.parse(doc));
      const invoiceIds = parsedInvoices.map((i) => i.id);

      if (alreadyAllSelected) {
        if (!isPaidOrPending) {
          dispatch(unSelectInvoice({ tabId: selectedTabId, invoices: parsedInvoices }));
        }
        dispatch(unSelectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds }));
        dispatch(unSelectCreditMemo({ tabId: selectedTabId, creditMemos: parsedCreditMemos }));
      } else {
        if (!isPaidOrPending) {
          dispatch(selectInvoice({ tabId: selectedTabId, invoices: parsedInvoices }));
        }
        dispatch(selectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds }));
        dispatch(selectCreditMemo({ tabId: selectedTabId, creditMemos: parsedCreditMemos }));
      }
    },
    [dispatch, selectedTabId, isPaidOrPending, integrationType]
  );

  const handleSendNote = async (message: string) => {
    if (!noteInvoice || !selectedMainBuyerId) return;
    setNoteLoading(true);
    try {
      const attachedFiles: { name: string; url: string; fileType: string }[] = [];
      for (const file of noteFilesRef.current) {
        const blobUrl = URL.createObjectURL(file);
        const fileName = `${new Date().getTime()}-${file.name}`;
        const downloadUrl = await firebaseFileUploadHelper({
          blobUrl,
          fileNameWithExtension: fileName,
          storagePath: `invoices/sendMessage/attachments/${selectedMainBuyerId}/${noteInvoice.id}`,
        });
        attachedFiles.push({ url: downloadUrl ?? '', name: fileName, fileType: file.type });
      }
      const result = await dispatch(
        sendMessagesInvoice({
          mainBuyerId: selectedMainBuyerId,
          invoiceId: noteInvoice.id,
          data: {
            message,
            date: new Date(),
            supplierId,
            buyerId: selectedMainBuyerId,
            recepientId: selectedMainBuyerId,
            isRead: false,
            attachements: attachedFiles,
            category: noteCategory,
          },
        })
      );
      if (result.type === 'suppliers/sendMessagesInvoice/fulfilled') {
        openDillToast({ message: 'Message sent successfully.', type: 'SUCCESS' });
        setNoteModalOpen(false);
      } else {
        openDillToast({ message: 'Failed to send message.', type: 'ERROR' });
      }
    } catch {
      openDillToast({ message: 'Failed to send message.', type: 'ERROR' });
    }
    setNoteLoading(false);
  };

  const totalSelectedCount =
    Object.values(selectedInvoices).flat().length +
    Object.values(selectedCreditMemos).flat().length;
  const downloadCount = Object.values(invoiceIdsForDownload).flat().length;

  const hasAnyBucketSelected = summaryDetails.some((summary) => {
    if (!summary.invoices || summary.invoices.length === 0) return false;
    const selectableBucketDocs = summary.invoices.filter((doc) => {
      const invCheck = QuickbooksInvoiceSchema.safeParse(doc);
      if (invCheck.success) {
        const isPendingReconciliation =
          invCheck.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
        const hasNegativeBalance = (invCheck.data.balance ?? 0) < 0;
        if (isPendingReconciliation && hasNegativeBalance) return false;
        return true;
      }
      const cm = QuickbooksCreditMemoSchema.safeParse(doc);
      return !isPaidOrPending && cm.success && isCreditMemoSelectable(cm.data, integrationType);
    });
    return selectableBucketDocs.some((doc) => currentTabSelectedIds.has(doc.id));
  });

  return (
    <>
      <AppNotesModal
        open={noteModalOpen}
        handleClose={() => setNoteModalOpen(false)}
        onSubmit={handleSendNote}
        title="Send Message"
        primaryButtonText="Send"
        loading={noteLoading}
        hasAttachment={true}
        onSelectFile={(files: File[]) => {
          noteFilesRef.current = files;
        }}
        hasDropDown={true}
        dropDownOptions={{
          label: 'Category',
          options: [
            { label: 'Dispute', value: 'Dispute' },
            { label: 'Other', value: 'Other' },
          ],
        }}
        onSelectDropDown={(value: string) => setNoteCategory(value || 'Other')}
      />

      {showSortSheet && (
        <SortSheet
          sortField={sortField}
          sortDir={sortDir}
          onSelect={handleSortSelect}
          onClear={handleSortClear}
          onClose={() => setShowSortSheet(false)}
        />
      )}

      {showJobsSheet && (
        <JobsFilterSheet
          jobs={selectedSupplierJobs}
          selectedIds={selectedJobIds}
          onClose={() => setShowJobsSheet(false)}
          onToggleJob={(id) => {
            const next = selectedJobIds.includes(id)
              ? selectedJobIds.filter((j) => j !== id)
              : [...selectedJobIds, id];
            onJobIdsChange(next);
          }}
          onClear={() => onJobIdsChange([])}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 pt-3 font-sans pb-4">
        {/* Jobs filter + Download + Sync — single row */}
        <div className="flex items-center justify-between mb-2 gap-2">
          {/* Left: Jobs filter button + active pills */}
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            {selectedSupplierJobs.length > 0 && (
              <button
                onClick={() => setShowJobsSheet(true)}
                className={[
                  'flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors flex-shrink-0',
                  activeJobIds.length > 0
                    ? 'bg-blue-50 text-blue-600 border-blue-300'
                    : 'bg-white text-gray-600 border-gray-200',
                ].join(' ')}>
                <Buildings size={14} className="text-blue-500" />
                Jobs Filter
                {activeJobIds.length > 0 && (
                  <span className="bg-blue-500 text-white text-[10px] rounded-full px-1.5 py-px font-medium">
                    {activeJobIds.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Right: sync */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <AppLastSyncDataView dataKey="invoices" from="buyer" />
          </div>
        </div>

        {/* Aging summary — scrollable chips */}
        {summaryDetails.length > 0 && (
          <div className="mb-3">
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
              {summaryDetails.map((summary) => {
                const hasInvoices = summary.invoices && summary.invoices.length > 0;
                const selectableBucketDocs = hasInvoices
                  ? summary.invoices.filter((doc) => {
                      const invCheck = QuickbooksInvoiceSchema.safeParse(doc);
                      if (invCheck.success) {
                        const isPendingReconciliation =
                          invCheck.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
                        const hasNegativeBalance = (invCheck.data.balance ?? 0) < 0;
                        if (isPendingReconciliation && hasNegativeBalance) return false;
                        return true;
                      }
                      const cm = QuickbooksCreditMemoSchema.safeParse(doc);
                      return (
                        !isPaidOrPending &&
                        cm.success &&
                        isCreditMemoSelectable(cm.data, integrationType)
                      );
                    })
                  : [];
                const selectedCount = selectableBucketDocs.filter((doc) =>
                  currentTabSelectedIds.has(doc.id)
                ).length;
                const isAllSelected =
                  selectableBucketDocs.length > 0 && selectedCount === selectableBucketDocs.length;

                return (
                  <button
                    key={summary.key}
                    onClick={() => handleBucketClick(summary, isAllSelected)}
                    disabled={!hasInvoices}
                    className={[
                      'flex-shrink-0 flex items-center gap-2 rounded-lg border px-3 py-2 transition-all text-left',
                      hasInvoices ? 'cursor-pointer' : 'cursor-default',
                      isAllSelected
                        ? 'border-2 border-blue-500 bg-blue-50'
                        : selectedCount > 0
                        ? 'border-blue-200 bg-blue-50/40'
                        : 'border-gray-200 bg-white',
                    ].join(' ')}>
                    {summary.icon && <div className="flex-shrink-0">{summary.icon}</div>}
                    <div>
                      <div className="text-[13px] font-semibold text-gray-900 leading-none">
                        {summary.value}
                      </div>
                      <div className="text-[10px] text-gray-400 leading-tight mt-0.5 whitespace-nowrap">
                        {summary.line1} {summary.line2}
                      </div>
                    </div>
                    {hasInvoices && (
                      <span
                        className={`ml-1 text-[11px] font-semibold whitespace-nowrap ${
                          isAllSelected
                            ? 'text-blue-500'
                            : selectedCount > 0
                            ? 'text-blue-400'
                            : 'text-gray-400'
                        }`}>
                        {isAllSelected
                          ? `✓ ${summary.invoices.length}`
                          : selectedCount > 0
                          ? `${selectedCount}/${summary.invoices.length}`
                          : summary.invoices.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {hasAnyBucketSelected && (
              <p className="text-[11px] text-blue-500 mt-1.5 font-medium">
                Invoices selected for aging category
              </p>
            )}
          </div>
        )}

        {/* Search (not sticky) */}
        <div className="mb-2">
          <div className="relative">
            <SearchIcon />
            <input
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 outline-none focus:border-gray-400 placeholder:text-gray-400"
              placeholder="Search documents, jobs…"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* Filters (not sticky) — content depends on active tab */}
        <div className="flex items-end gap-2 mb-3 overflow-x-auto pb-0.5">
          {/* PAID tab: Invoice Date selector */}
          {selectedTabId === 'PAID' && (
            <div className="flex-shrink-0 w-44">
              <DillDropdown
                label="Invoice Date"
                items={weeksOptions}
                value={String(buyerSupplierInvoicesDataWeeksLimit)}
                onSelectChange={(item) => onWeeksLimitChange(Number(item.value))}
                isSearchable={false}
              />
            </div>
          )}

          {/* OPEN tab only: show open + show past due toggles */}
          {selectedTabId === 'OPEN' && (
            <>
              <button
                onClick={() =>
                  onInvoiceFilterChange(
                    selectedInvoiceFilterId === 'SHOW_OPEN' ? null : 'SHOW_OPEN'
                  )
                }
                className={[
                  'flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors flex-shrink-0',
                  selectedInvoiceFilterId === 'SHOW_OPEN'
                    ? 'bg-blue-50 text-blue-600 border-blue-300'
                    : 'bg-white text-gray-500 border-gray-200',
                ].join(' ')}>
                <div
                  className={[
                    'w-3.5 h-3.5 rounded border flex items-center justify-center',
                    selectedInvoiceFilterId === 'SHOW_OPEN'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'border-gray-300',
                  ].join(' ')}>
                  {selectedInvoiceFilterId === 'SHOW_OPEN' && <CheckIcon />}
                </div>
                Show all open invoices
              </button>
              <button
                onClick={() =>
                  onInvoiceFilterChange(
                    selectedInvoiceFilterId === 'SHOW_PAST_DUE' ? null : 'SHOW_PAST_DUE'
                  )
                }
                className={[
                  'flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors flex-shrink-0',
                  selectedInvoiceFilterId === 'SHOW_PAST_DUE'
                    ? 'bg-blue-50 text-blue-600 border-blue-300'
                    : 'bg-white text-gray-500 border-gray-200',
                ].join(' ')}>
                <div
                  className={[
                    'w-3.5 h-3.5 rounded border flex items-center justify-center',
                    selectedInvoiceFilterId === 'SHOW_PAST_DUE'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'border-gray-300',
                  ].join(' ')}>
                  {selectedInvoiceFilterId === 'SHOW_PAST_DUE' && <CheckIcon />}
                </div>
                Show past due invoices
              </button>
            </>
          )}
        </div>

        {/* Sticky bar — tabs + select all + bulk actions */}
        <div className="sticky top-0 z-10 bg-white -mx-4 px-4 pt-2 pb-6 mb-3 border-b border-gray-100">
          {/* Tabs — pill container style */}
          <div className="bg-gray-100 rounded-full p-1 flex items-stretch gap-0.5 mb-2 overflow-x-auto">
            {invoicesTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={[
                  'flex-1 flex flex-col items-center justify-center px-3 py-1.5 rounded-full transition-all whitespace-nowrap min-w-0',
                  selectedTabId === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-transparent text-gray-500',
                ].join(' ')}>
                <div className="flex items-center gap-1 leading-tight">
                  <span className="text-[13px] font-medium">{tab.name}</span>
                  <span
                    className={`text-[11px] font-medium px-1.5 py-px rounded-full leading-none ${
                      selectedTabId === tab.id
                        ? 'bg-black/10 text-gray-700'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                    {tab.invoices.length}
                  </span>
                </div>
                <span
                  className={`text-[11px] leading-tight ${
                    selectedTabId === tab.id ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                  {formatMoney(tab.totalAmount.toString(), 2)}
                </span>
              </button>
            ))}
          </div>

          {/* Select all + bulk actions */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <DillCheckbox
                isChecked={selectAll}
                indeterminate={indeterminate}
                onChange={toggleAll}
              />
              <span className="text-[13px] text-gray-500 text-nowrap">Select all</span>
              {totalSelectedCount > 0 && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 font-medium leading-none">
                  {totalSelectedCount}
                </span>
              )}
            </label>

            <button
              onClick={() => setShowSortSheet(true)}
              className={[
                'flex items-center gap-1 text-[12px] px-2 py-1 rounded-full border transition-colors flex-shrink-0',
                sortField
                  ? 'bg-blue-50 text-blue-600 border-blue-300'
                  : 'bg-white text-gray-500 border-gray-200',
              ].join(' ')}>
              <ArrowsDownUp size={13} />
              Sort
              {sortField && (
                <span className="bg-blue-500 text-white text-[10px] rounded-full px-1.5 py-px font-medium leading-none">
                  1
                </span>
              )}
            </button>

            <div className="flex items-center gap-3 ml-auto">
              {statementPdfsOptions.length > 0 && activeJobIds.length === 0 ? (
                <DillDropdownButton
                  options={[...statementPdfsOptions]}
                  icon={<FilePlus size={16} />}
                  onSelect={(option) => {
                    if (option.value === 'TODAY') {
                      onDownloadStatement();
                    } else if (option.value) {
                      let fileName = option.extraData;
                      if (!fileName) {
                        const decodedUrl = decodeURIComponent(option.value);
                        const parts = decodedUrl.split('/');
                        fileName = `${parts[parts.length - 1]}.pdf`;
                      }
                      downloadFile(option.value, fileName);
                    }
                  }}
                  variant="secondary"
                  height={32}
                  width={32}
                />
              ) : (
                <DillButton
                  icon={<FilePlus size={16} />}
                  variant="secondary"
                  onClick={onDownloadStatement}
                  height={32}
                  width={32}
                />
              )}
              <div className="relative">
                {downloadCount === 1 ? (
                  <DillButton
                    onClick={() => {
                      if (downloadCount > 0) onBulkDownload(true);
                    }}
                    variant="secondary"
                    label="Bulk Download"
                    height={32}
                  />
                ) : (
                  <DillDropdownButton
                    options={[
                      { label: 'Separate files', value: 'separate' },
                      { label: 'One file', value: 'onefile' },
                    ]}
                    onSelect={(option) => {
                      if (downloadCount > 0) onBulkDownload(option.value === 'onefile');
                    }}
                    variant="secondary"
                    label="Bulk Download"
                    height={32}
                  />
                )}
                <div className="PRIMARY_500-BG WHITE-CLR absolute -top-2 right-[-12px] flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white text-2xs font-bold z-10">
                  {downloadCount}
                </div>
              </div>
              <div className="relative">
                <DillButton
                  label="Bulk Pay"
                  onClick={() => {
                    if (totalSelectedCount > 0) onBulkPay();
                  }}
                  height={32}
                  isDisabled={totalSelectedCount === 0 || !buyerUserCanPay || !isSupplierVerified}
                />
                <div className="PRIMARY_500-BG WHITE-CLR absolute -top-2 right-[-12px] flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white text-2xs font-bold z-10">
                  {totalSelectedCount}
                </div>
                <div className="absolute -bottom-5 right-0 flex flex-col items-end gap-0.5">
                  <span className="TEXT_SECONDARY-CLR text-xs whitespace-nowrap">
                    {formatMoney(accumulateAmount.toString(), 2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice list */}
        {sortedInvoices.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {sortedInvoices.map((doc, index) => {
              const info = getDocDisplayInfo(doc);
              const isSelected = currentTabSelectedIds.has(doc.id);
              const docInvCheck = QuickbooksInvoiceSchema.safeParse(doc);
              const docCmCheck = QuickbooksCreditMemoSchema.safeParse(doc);
              const isPendingReconciliationNegative =
                docInvCheck.success &&
                docInvCheck.data.dillPaymentStatus === 'RECONCILIATION_PENDING' &&
                (docInvCheck.data.balance ?? 0) < 0;
              const isSelectable =
                (docInvCheck.success && !isPendingReconciliationNegative) ||
                (docCmCheck.success && isCreditMemoSelectable(docCmCheck.data, integrationType));
              const disabledReason = isPendingReconciliationNegative
                ? 'This invoice is pending reconciliation with a negative balance and cannot be selected.'
                : undefined;
              const isCvsPlumbingDiscountTerm =
                docInvCheck.success &&
                CVS_PLUMBING_DISCOUNT_TERMS.includes(docInvCheck.data.terms ?? '');
              const discountAvailable =
                (docInvCheck.success &&
                  NET_TERMS_REGEX.test(docInvCheck.data.terms ?? '') &&
                  DISCOUNT_INTEGRATIONS.includes(integrationType ?? '')) ||
                isCvsPlumbingDiscountTerm;
              return (
                <InvoiceCard
                  key={doc.id || index}
                  info={info}
                  checked={isSelected}
                  selectable={isSelectable}
                  disabledReason={disabledReason}
                  discountAvailable={discountAvailable}
                  onCheck={() => toggleSelect(doc)}
                  isOpen={expanded.has(doc.id)}
                  onToggle={() => toggleExpand(doc.id)}
                  onPay={() => {
                    const check = QuickbooksInvoiceSchema.safeParse(doc);
                    if (check.success) onPaySingle(check.data);
                  }}
                  onView={() => {
                    if (info.isPdfAvailable) {
                      const invCheck = QuickbooksInvoiceSchema.safeParse(doc);
                      const cmCheck = QuickbooksCreditMemoSchema.safeParse(doc);
                      if (invCheck.success) setActiveInvoice(invCheck.data);
                      else if (cmCheck.success) setActiveCreditMemo(cmCheck.data);
                      handlePreviewPdf({
                        type: info.docType,
                        id: info.id,
                        docNumber: info.docNumber,
                        signedPdfs: info.signedPdfs,
                      });
                    } else {
                      onView(doc);
                    }
                  }}
                  onDownload={() =>
                    handleDownloadPdf({
                      type: info.docType,
                      id: info.id,
                      docNumber: info.docNumber,
                      signedPdfs: info.signedPdfs,
                    })
                  }
                  onPrint={() =>
                    handlePrintPdf({
                      type: info.docType,
                      id: info.id,
                      docNumber: info.docNumber,
                      signedPdfs: info.signedPdfs,
                    })
                  }
                  onNote={() => {
                    const check = QuickbooksInvoiceSchema.safeParse(doc);
                    if (check.success) {
                      setNoteInvoice(check.data);
                      setNoteModalOpen(true);
                    }
                  }}
                  canPay={!isPaidOrPending && isSupplierVerified && buyerUserCanPay && info.canPay}
                />
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
            No invoices found
          </div>
        )}
      </div>

      <AppPdfListModal
        open={openPdfListModal}
        handleClose={() => {
          setOpenPdfListModal(false);
          navigate(location.pathname);
        }}
        pdfList={downloadPdfList}
        invoice={selectedDocNumber}
        handleDownloadPdf={handleDownloadSelectedPdf}
        buttonText={printMode ? 'Print' : 'Download'}
      />
      <AppPdfPreviewModal
        open={openPdfPreviewModal}
        mode="Buyer"
        handleClose={() => {
          setOpenPdfPreviewModal(false);
          setActiveInvoice(null);
          setActiveCreditMemo(null);
          navigate(location.pathname);
        }}
        invoice={
          activeInvoice
            ? activeInvoice.docNumber || ''
            : activeCreditMemo
            ? activeCreditMemo.docNumber || ''
            : ''
        }
        selectedItem={activeInvoice || activeCreditMemo || null}
        pdfType={activeInvoice ? 'INVOICE' : activeCreditMemo ? 'CREDIT_MEMO' : undefined}
        pdfList={previewPdfList}
        handleDownloadPdf={handleDownloadSelectedPdf}
        isSigned={invoiceSigned}
        supplierDomainId={supplierDomainId}
        onPay={
          activeInvoice
            ? () => {
                onPaySingle(activeInvoice);
                setOpenPdfPreviewModal(false);
              }
            : undefined
        }
      />
    </>
  );
}
